/**
 * Typed wrapper over the Box3D wasm shim (see shim.c / build_physics.sh).
 * Pure interface over an already-instantiated emscripten module so the
 * browser client and the headless Deno survivability tests share it.
 */
import type { CollisionProxy } from "../../gen/endless/proxies.ts";

/** The emscripten module surface the shim exports. */
/** Hit-event readback capacity per frame (plenty: a 240-body pack rarely
 * lands 20 threshold-beating knocks in one step). */
const HITS_CAP = 256;

export interface PhysicsModule {
  _ph_init(gravityY: number): void;
  /** Added with native lifecycle teardown. Optional so a browser that has
   * a cached pre-teardown module can still regenerate instead of crashing. */
  _ph_dispose?(): void;
  _ph_begin_static(key: number): void;
  _ph_add_box(
    cx: number,
    cy: number,
    cz: number,
    hx: number,
    hy: number,
    hz: number,
    yawQuarter: number,
  ): void;
  _ph_add_slab(
    cx: number,
    cy: number,
    cz: number,
    hx: number,
    hy: number,
    hz: number,
    yawRad: number,
    pitchRad: number,
  ): void;
  _ph_end_static(): void;
  _ph_remove_static(key: number): void;
  _ph_spawn_marble(
    idx: number,
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    radius: number,
  ): void;
  _ph_destroy_marble(idx: number): void;
  _ph_marble_ghost(idx: number, ghost: number): void;
  /** Optional for previously cached native modules. */
  _ph_marble_segment?(idx: number, key: number): void;
  _ph_add_box_q(
    cx: number,
    cy: number,
    cz: number,
    hx: number,
    hy: number,
    hz: number,
    qx: number,
    qy: number,
    qz: number,
    qw: number,
  ): void;
  _ph_add_bumper_q(
    cx: number,
    cy: number,
    cz: number,
    radius: number,
    height: number,
    restitution: number,
    qx: number,
    qy: number,
    qz: number,
    qw: number,
  ): void;
  _ph_add_mesh(
    vertsPtr: number,
    vertCount: number,
    trisPtr: number,
    triCount: number,
  ): number;
  _ph_add_bumper(
    cx: number,
    cy: number,
    cz: number,
    radius: number,
    height: number,
    restitution: number,
  ): void;
  _ph_marble_force(idx: number, fx: number, fy: number, fz: number): void;
  _ph_marble_jump(
    idx: number,
    nx: number,
    ny: number,
    nz: number,
    target: number,
  ): void;
  _ph_marble_mass(idx: number): number;
  _ph_marble_restitution(idx: number, restitution: number): void;
  _ph_gravity(gx: number, gy: number, gz: number): void;
  _ph_step(dt: number, substeps: number): void;
  _ph_marble_state(idx: number, outPtr: number): void;
  _ph_states(outPtr: number, n: number): void;
  _ph_hits(outPtr: number, cap: number): number;
  _malloc(bytes: number): number;
  _free(ptr: number): void;
  HEAPF64: Float64Array;
  HEAPF32: Float32Array;
  HEAP32: Int32Array;
}

export interface MarbleState {
  pos: [number, number, number];
  vel: [number, number, number];
  quat: [number, number, number, number];
}

/** Physics marble radius — HALF the kit mesh's (user: "shrink the ball
 * 50%"); the render mesh scales by MARBLE_SCALE to match. */
export const MARBLE_RADIUS = 0.1;
/** Visual scale applied to the kit's marble piece. */
export const MARBLE_SCALE = 0.5;
/** Default marble restitution (shim spawn value); shock zones override. */
export const MARBLE_RESTITUTION = 0.35;
/** Marble Blast's gravity at our scale (MBHaxe: _gravity = 20) — adopted
 * together with the smooth ribbon, per docs/physics.md. */
export const GRAVITY_Y = -20;
/** Fixed physics clock: 60 Hz × 4 substeps (the spike's validated rate). */
export const FIXED_DT = 1 / 60;
export const SUBSTEPS = 4;

/**
 * The assist law, shared by the client driver and the survivability tests
 * (ONE law, one truth): keep the marble rolling at cruising speed on
 * flats, carry it up climbs (the track lifts, like classic marble-run
 * conveyors — gravity-along-slope is cancelled plus a net push), and
 * brake gently when gravity has piled on too much speed.
 * Returns a world-space force or null.
 */
export function assistForce(
  vel: readonly [number, number, number],
  tangent: readonly [number, number, number],
  mass: number,
  /** Steepest upcoming slope (rail lookahead) — the climb carry must be
   * sized for the ramp AHEAD, not the base station, or the marble cycles
   * at the foot of every ramp (under-powered mid-climb, rolls back). */
  climbTy: number = tangent[1],
  /** Radians of heading change over the next ~4 units of rail — fast
   * arrivals into tight curves launch over the walls; brake to a safe
   * entry speed instead (the driver-side speed envelope). */
  curveAhead = 0,
  /** Per-tile speed modifier (M11): scales the cruise target AND the
   * assist authority — a boost zone actually accelerates the marble to
   * launch speed (force-scaling alone can't pass the target check). */
  speedScale = 1,
): [number, number, number] | null {
  // Near-vertical rail (drop shafts, steep chute dives ty ≥ 0.77): the
  // fall is gravity's job — an assist here just grinds the marble into
  // the cup. Climbs top out at ty ≈ 0.63 (ramp-long-c mid-cubic), so 0.7
  // keeps the climb carry alive through the WHOLE climb — at 0.6 it cut
  // out mid-slope and marbles cycled at the base of 1.0-climbs.
  if (Math.abs(tangent[1]) > 0.7) return null;
  const vAlong = vel[0] * tangent[0] + vel[1] * tangent[1] +
    vel[2] * tangent[2];
  // Cruise/brake tuned to the proven survivable envelope. M11 NOTE: a
  // global speed raise (4.2/10) launched marbles off curve seams and
  // crest lips — dynamic speed needs CURVATURE-AWARE braking (the speed
  // envelope) and banked/taller walls, not a bigger constant.
  const target = 3.2 * speedScale;
  const MAX = 9;
  const safe = Math.max(target, MAX - curveAhead * 5.5);
  if (vAlong > safe) {
    const f = mass * Math.min(10, 3 + (vAlong - safe) * 2);
    return [-tangent[0] * f, -tangent[1] * f, -tangent[2] * f];
  }
  const climbing = tangent[1] > 0.12 || climbTy > 0.12;
  if (climbing && vAlong < target) {
    // Cancel the along-slope gravity component and push a little extra.
    const a = -GRAVITY_Y * Math.max(tangent[1], climbTy) + 2.6;
    const f = mass * a;
    return [tangent[0] * f, tangent[1] * f, tangent[2] * f];
  }
  if (vAlong < target) {
    // Marble Blast responsiveness = authority (their angular accel ≈ 15
    // linear); scaled to our slalom widths.
    const f = mass * 6 * speedScale;
    return [tangent[0] * f, tangent[1] * f, tangent[2] * f];
  }
  return null;
}

export class PhysicsEngine {
  private readonly out: number;
  private batchPtr = 0;
  private batchCap = 0;
  private accumulator = 0;
  /** Per-marble restitution cache (index → last set value). */
  private readonly restitutions = new Map<number, number>();
  /** Per-marble ghost cache (respawn immunity; spawn resets to solid). */
  private readonly ghosts = new Map<number, boolean>();
  private readonly trackSegments = new Map<number, number>();
  private disposed = false;

  constructor(private readonly m: PhysicsModule) {
    // The C shim is a singleton world; ph_init tears down any forgotten
    // previous owner before creating the next one.
    m._ph_init(GRAVITY_Y);
    this.out = m._malloc(10 * 8);
  }

  /** Release the singleton Box3D world and every JS-owned WASM buffer. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.batchPtr !== 0) this.m._free(this.batchPtr);
    if (this.hitsPtr !== 0) this.m._free(this.hitsPtr);
    this.m._free(this.out);
    this.batchPtr = 0;
    this.batchCap = 0;
    this.hitsPtr = 0;
    this.restitutions.clear();
    this.ghosts.clear();
    this.trackSegments.clear();
    this.m._ph_dispose?.();
  }

  addSegment(key: number, proxies: readonly CollisionProxy[]): void {
    this.m._ph_begin_static(key);
    for (const p of proxies) {
      if (p.kind === "mesh") {
        // Box3D copies vertex data into its own BVH storage — the heap
        // staging buffers are freed immediately.
        const vp = this.m._malloc(p.vertices.length * 4);
        this.m.HEAPF32.set(p.vertices, vp / 4);
        const ip = this.m._malloc(p.indices.length * 4);
        this.m.HEAP32.set(p.indices, ip / 4);
        const ok = this.m._ph_add_mesh(
          vp,
          p.vertices.length / 3,
          ip,
          p.indices.length / 3,
        );
        this.m._free(vp);
        this.m._free(ip);
        if (ok !== 1) {
          throw new Error(`mesh creation failed for segment ${key} (${ok})`);
        }
        continue;
      }
      if (p.kind === "bumper") {
        if (p.quat) {
          this.m._ph_add_bumper_q(
            p.center[0],
            p.center[1],
            p.center[2],
            p.radius,
            p.height,
            p.restitution,
            p.quat[0],
            p.quat[1],
            p.quat[2],
            p.quat[3],
          );
        } else {
          this.m._ph_add_bumper(
            p.center[0],
            p.center[1],
            p.center[2],
            p.radius,
            p.height,
            p.restitution,
          );
        }
        continue;
      }
      // Reoriented chunks (M16) carry a full quaternion — box and slab
      // land on the same oriented-hull path (a slab IS a box hull; its
      // yaw/pitch were folded into the quat by worldifyProxies).
      if (p.quat) {
        this.m._ph_add_box_q(
          p.center[0],
          p.center[1],
          p.center[2],
          p.half[0],
          p.half[1],
          p.half[2],
          p.quat[0],
          p.quat[1],
          p.quat[2],
          p.quat[3],
        );
      } else if (p.kind === "box") {
        this.m._ph_add_box(
          p.center[0],
          p.center[1],
          p.center[2],
          p.half[0],
          p.half[1],
          p.half[2],
          0,
        );
      } else {
        this.m._ph_add_slab(
          p.center[0],
          p.center[1],
          p.center[2],
          p.half[0],
          p.half[1],
          p.half[2],
          p.yaw,
          p.pitch,
        );
      }
    }
    this.m._ph_end_static();
  }

  removeSegment(key: number): void {
    this.m._ph_remove_static(key);
  }

  /** Marble mass (kg — Box3D defaults to SI density 1000). */
  massOf(idx: number): number {
    return this.m._ph_marble_mass(idx);
  }

  /** Marble 0's mass (single-marble drivers). */
  get mass(): number {
    return this.m._ph_marble_mass(0);
  }

  /** Shock-absorber override (cached — the zone law calls this per step). */
  setRestitution(idx: number, value: number): void {
    if (this.restitutions.get(idx) === value) return;
    this.restitutions.set(idx, value);
    this.m._ph_marble_restitution(idx, value);
  }

  /** Spawn (or respawn) marble `idx`. World gravity is FIXED at
   * [0, GRAVITY_Y, 0] — per-marble gravity zones are delta forces
   * (rail.ts stationGravityDelta), one global vector cannot serve N
   * marbles in different zones. */
  spawnMarble(
    idx: number,
    pos: readonly [number, number, number],
    vel: readonly [number, number, number],
  ): void {
    if (idx === 0) this.accumulator = 0;
    this.restitutions.set(idx, MARBLE_RESTITUTION); // shim recreates the shape
    this.ghosts.set(idx, false);
    this.trackSegments.delete(idx);
    this.m._ph_spawn_marble(
      idx,
      pos[0],
      pos[1],
      pos[2],
      vel[0],
      vel[1],
      vel[2],
      MARBLE_RADIUS,
    );
  }

  destroyMarble(idx: number): void {
    this.m._ph_destroy_marble(idx);
    this.restitutions.delete(idx);
    this.ghosts.delete(idx);
    this.trackSegments.delete(idx);
  }

  /** Limit track contact to this checkpoint and its connected neighbors.
   * Ghost changes retain this mask; respawns reset the cached ownership. */
  setTrackSegment(idx: number, key: number): void {
    if (this.trackSegments.get(idx) === key) return;
    this.trackSegments.set(idx, key);
    this.m._ph_marble_segment?.(idx, key);
  }

  /** Respawn immunity (cached): a ghost marble keeps colliding with the
   * track but passes through other marbles. Spawn resets to solid. */
  setGhost(idx: number, ghost: boolean): void {
    if ((this.ghosts.get(idx) ?? false) === ghost) return;
    this.ghosts.set(idx, ghost);
    this.m._ph_marble_ghost(idx, ghost ? 1 : 0);
  }

  /**
   * Advance by wall-clock dt on the fixed clock; `eachStep` runs before
   * every fixed step (assist forces are per-step so behavior is
   * frame-rate independent). Returns the number of fixed steps taken.
   */
  step(dt: number, eachStep?: () => void): number {
    this.accumulator = Math.min(this.accumulator + dt, 0.25);
    let steps = 0;
    while (this.accumulator >= FIXED_DT) {
      eachStep?.();
      this.m._ph_step(FIXED_DT, SUBSTEPS);
      this.accumulator -= FIXED_DT;
      steps++;
    }
    return steps;
  }

  force(idx: number, fx: number, fy: number, fz: number): void {
    this.m._ph_marble_force(idx, fx, fy, fz);
  }

  /** Top-up jump (M14b): velocity along `n` is SET to `target`, never
   * added — deterministic leave-speed (the MBHaxe jump contract), so the
   * autopilot can price an arc before committing. Safe to call repeatedly
   * while the condition holds; extra calls are no-ops. */
  jump(
    idx: number,
    n: readonly [number, number, number],
    target: number,
  ): void {
    this.m._ph_marble_jump(idx, n[0], n[1], n[2], target);
  }

  /** Batched state readback for large packs: ONE wasm call for marbles
   * [0, n); returns a dense HEAP VIEW [i*10 .. i*10+9] = px py pz vx vy
   * vz qx qy qz qw. The view is invalidated by the next call and by any
   * heap growth — consume it within the step. */
  private hitsPtr = 0;

  statesAll(n: number): Float64Array {
    if (n > this.batchCap) {
      if (this.batchPtr !== 0) this.m._free(this.batchPtr);
      this.batchCap = n;
      this.batchPtr = this.m._malloc(n * 10 * 8);
    }
    this.m._ph_states(this.batchPtr, n);
    return this.m.HEAPF64.subarray(
      this.batchPtr / 8,
      this.batchPtr / 8 + n * 10,
    );
  }

  /** Contact hit events since the last step (contact-events round):
   * a dense HEAP VIEW of `count` × 6 floats [idxA, idxB (−1 = static),
   * approachSpeed, px, py, pz]. Hits report only above the world's
   * threshold (~1.5 u/s — real knocks, not rolling contact). Consume
   * within the frame; the view dies on the next call or heap growth. */
  hits(): { count: number; data: Float32Array } {
    if (this.hitsPtr === 0) {
      this.hitsPtr = this.m._malloc(HITS_CAP * 6 * 4);
    }
    const count = this.m._ph_hits(this.hitsPtr, HITS_CAP);
    return {
      count,
      data: this.m.HEAPF32.subarray(
        this.hitsPtr / 4,
        this.hitsPtr / 4 + count * 6,
      ),
    };
  }

  state(idx = 0): MarbleState {
    this.m._ph_marble_state(idx, this.out);
    const h = this.m.HEAPF64;
    const i = this.out / 8;
    return {
      pos: [h[i], h[i + 1], h[i + 2]],
      vel: [h[i + 3], h[i + 4], h[i + 5]],
      quat: [h[i + 6], h[i + 7], h[i + 8], h[i + 9]],
    };
  }
}
